import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType } from 'docx';
import { saveAs } from 'file-saver';
import { marked } from 'marked';

/**
 * Export markdown content to a DOCX file
 */
export async function exportToDocx(
  markdownContent: string,
  title: string,
  policyLevel: string
): Promise<void> {
  try {
    // Step 1: Convert markdown to HTML using marked
    const html = marked.parse(markdownContent, { async: false }) as string;
    
    // Step 2: Parse HTML using DOMParser
    const parser = new DOMParser();
    const doc_html = parser.parseFromString(html, 'text/html');
    
    // Step 3: Convert HTML elements to Word elements
    const children: (Paragraph | Table)[] = [];
    
    // Helper: Extract text runs from an element, preserving formatting
    const extractTextRuns = (element: Node, inheritBold = false, inheritItalic = false): TextRun[] => {
      const runs: TextRun[] = [];
      
      element.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent || '';
          if (text) {
            runs.push(new TextRun({ 
              text, 
              bold: inheritBold,
              italics: inheritItalic 
            }));
          }
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const el = child as HTMLElement;
          const tagName = el.tagName.toLowerCase();
          
          let newBold = inheritBold;
          let newItalic = inheritItalic;
          
          if (tagName === 'strong' || tagName === 'b') {
            newBold = true;
          }
          if (tagName === 'em' || tagName === 'i') {
            newItalic = true;
          }
          
          runs.push(...extractTextRuns(el, newBold, newItalic));
        }
      });
      
      return runs;
    };
    
    // Helper: Process a table element
    const processTableElement = (tableEl: HTMLTableElement): Table => {
      const rows: TableRow[] = [];
      
      const thead = tableEl.querySelector('thead');
      if (thead) {
        thead.querySelectorAll('tr').forEach((tr) => {
          const cells: TableCell[] = [];
          tr.querySelectorAll('th, td').forEach((cell) => {
            cells.push(new TableCell({
              children: [new Paragraph({ 
                children: extractTextRuns(cell, true)
              })],
              width: { size: Math.floor(100 / tr.children.length), type: WidthType.PERCENTAGE },
            }));
          });
          if (cells.length > 0) {
            rows.push(new TableRow({ children: cells }));
          }
        });
      }
      
      const tbody = tableEl.querySelector('tbody') || tableEl;
      tbody.querySelectorAll('tr').forEach((tr) => {
        if (thead && tr.closest('thead')) return;
        
        const cells: TableCell[] = [];
        tr.querySelectorAll('th, td').forEach((cell) => {
          const isHeader = cell.tagName.toLowerCase() === 'th';
          cells.push(new TableCell({
            children: [new Paragraph({ 
              children: extractTextRuns(cell, isHeader)
            })],
            width: { size: Math.floor(100 / tr.children.length), type: WidthType.PERCENTAGE },
          }));
        });
        if (cells.length > 0) {
          rows.push(new TableRow({ children: cells }));
        }
      });
      
      return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: rows,
      });
    };
    
    // Helper: Process list items
    const processListItems = (listEl: HTMLElement): Paragraph[] => {
      const paragraphs: Paragraph[] = [];
      listEl.querySelectorAll(':scope > li').forEach((li) => {
        paragraphs.push(new Paragraph({
          children: extractTextRuns(li),
          bullet: { level: 0 },
          spacing: { before: 60, after: 60 },
        }));
      });
      return paragraphs;
    };
    
    // Process all top-level elements
    doc_html.body.childNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        const text = node.textContent?.trim();
        if (text) {
          children.push(new Paragraph({
            children: [new TextRun({ text })],
            spacing: { before: 120, after: 120 },
          }));
        }
        return;
      }
      
      const element = node as HTMLElement;
      const tagName = element.tagName.toLowerCase();
      
      switch (tagName) {
        case 'h1':
          children.push(new Paragraph({
            children: extractTextRuns(element),
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 240, after: 120 },
          }));
          break;
        case 'h2':
          children.push(new Paragraph({
            children: extractTextRuns(element),
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 240, after: 120 },
          }));
          break;
        case 'h3':
          children.push(new Paragraph({
            children: extractTextRuns(element),
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 200, after: 100 },
          }));
          break;
        case 'h4':
        case 'h5':
        case 'h6':
          children.push(new Paragraph({
            children: extractTextRuns(element, true),
            spacing: { before: 200, after: 100 },
          }));
          break;
        case 'p':
          children.push(new Paragraph({
            children: extractTextRuns(element),
            spacing: { before: 120, after: 120 },
          }));
          break;
        case 'ul':
        case 'ol':
          children.push(...processListItems(element));
          break;
        case 'table':
          children.push(processTableElement(element as HTMLTableElement));
          children.push(new Paragraph({ text: '' }));
          break;
        case 'blockquote':
          children.push(new Paragraph({
            children: extractTextRuns(element, false, true),
            indent: { left: 720 },
            spacing: { before: 120, after: 120 },
          }));
          break;
        case 'hr':
          children.push(new Paragraph({
            children: [new TextRun({ text: '─'.repeat(50) })],
            spacing: { before: 240, after: 240 },
          }));
          break;
        case 'pre':
        case 'code':
          children.push(new Paragraph({
            children: [new TextRun({ 
              text: element.textContent || '',
              font: 'Courier New',
            })],
            spacing: { before: 120, after: 120 },
          }));
          break;
        default:
          const textContent = element.textContent?.trim();
          if (textContent) {
            children.push(new Paragraph({
              children: extractTextRuns(element),
              spacing: { before: 120, after: 120 },
            }));
          }
      }
    });

    const doc = new Document({
      creator: 'AI Policy Producer',
      title: title || 'AI Policy',
      description: 'AI Policy Document',
      sections: [{
        properties: {},
        children: [
          // Add title at the top
          new Paragraph({
            text: title || 'AI Policy',
            heading: HeadingLevel.TITLE,
            spacing: { after: 240 },
          }),
          ...children,
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    const fileName = `AI_Policy_${policyLevel}_${new Date().toISOString().split('T')[0]}.docx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error('Failed to export to DOCX:', error);
    throw new Error('Failed to export to DOCX. Please try again.');
  }
}

